import React, { useState } from "react";
import axios from 'axios';
import { Mail, MapPin, Send } from "react-feather";
import Sectiontitle from "../components/Sectiontitle";
import Layout from "../components/Layout";
import { Fade, Bounce } from 'react-awesome-reveal';
import { useLanguage } from '../context/LanguageContext';
import apyKeys from "../data/emailKeys";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LAST_SUBMIT_KEY = 'contact_last_submit_timestamp';
const COOLDOWN_SECONDS = 60; // Cooldown de 60 segundos entre envíos

function Contact() {
  const { lang, d } = useLanguage();

  const [formdata, setFormdata] = useState({
    username: "",
    email: "",
    subject: "",
    message: "",
    hp_trap: "" // Campo Honeypot trampas para bots
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const setErrorToast = (message, type) => {
    switch (type) {
      case 'e':
        toast.error(message, { autoClose: 3000 });
        break;
      case 'i':
        toast.info(message, { autoClose: 3000 });
        break;
      default:
        toast.warning(message, { autoClose: 3000 });
        break;
    }
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    // 1. Trampa Honeypot contra Spam Bots
    if (formdata.hp_trap) {
      setErrorToast(d.contactInfo.alerts.mesageSent, 'i');
      setFormdata({ username: "", email: "", subject: "", message: "", hp_trap: "" });
      return;
    }

    // 2. Validación de campos obligatorios
    if (!formdata.username) {
      setErrorToast(d.contactInfo.alerts.nameRequired, 'a');
      return;
    } else if (!formdata.email) {
      setErrorToast(d.contactInfo.alerts.emailRequired, 'a');
      return;
    } else if (!formdata.subject) {
      setErrorToast(d.contactInfo.alerts.subjectRequired, 'a');
      return;
    } else if (!formdata.message) {
      setErrorToast(d.contactInfo.alerts.messageRequired, 'a');
      return;
    } else {
      const pattern = new RegExp(/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);
      if (!pattern.test(formdata.email)) {
        setErrorToast(d.contactInfo.alerts.validEmail, 'a');
        return;
      }
    }

    // 3. Cooldown Rate Limiting
    const lastSubmitTime = localStorage.getItem(LAST_SUBMIT_KEY);
    if (lastSubmitTime) {
      const elapsedSeconds = Math.floor((Date.now() - parseInt(lastSubmitTime, 10)) / 1000);
      if (elapsedSeconds < COOLDOWN_SECONDS) {
        const waitSec = COOLDOWN_SECONDS - elapsedSeconds;
        const msg = lang === 'es'
          ? `Por favor espera ${waitSec} segundos antes de enviar otro mensaje.`
          : `Please wait ${waitSec} seconds before sending another message.`;
        setErrorToast(msg, 'a');
        return;
      }
    }

    setIsSubmitting(true);

    const payload = {
      username: formdata.username,
      email: formdata.email,
      subject: formdata.subject,
      message: formdata.message
    };

    // Método 1: Servidor propio GoDaddy (/api/contact.php)
    try {
      const response = await axios.post('/api/contact.php', payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 8000
      });
      if (response.data && response.data.status === 'success') {
        localStorage.setItem(LAST_SUBMIT_KEY, Date.now().toString());
        setErrorToast(d.contactInfo.alerts.mesageSent, 'i');
        setFormdata({ username: "", email: "", subject: "", message: "", hp_trap: "" });
        setIsSubmitting(false);
        return;
      }
    } catch (phpErr) {
      console.warn("PHP mailer endpoint not reachable or failed, trying EmailJS fallback...", phpErr);
    }

    // Método 2: EmailJS Fallback API
    try {
      const emailJsData = {
        service_id: apyKeys.SERVICE_ID,
        template_id: apyKeys.IDTEMPLATE_ID,
        user_id: apyKeys.USER_ID,
        template_params: payload
      };
      await axios.post('https://api.emailjs.com/api/v1.0/email/send', emailJsData, { timeout: 8000 });
      localStorage.setItem(LAST_SUBMIT_KEY, Date.now().toString());
      setErrorToast(d.contactInfo.alerts.mesageSent, 'i');
      setFormdata({ username: "", email: "", subject: "", message: "", hp_trap: "" });
      setIsSubmitting(false);
      return;
    } catch (emailJsErr) {
      console.warn("EmailJS fallback failed:", emailJsErr);
    }

    // Método 3: Fallback cliente mailto (Garantiza que NUNCA falle un mensaje)
    try {
      const mailtoSubject = encodeURIComponent(`[Web Contact] ${formdata.subject}`);
      const mailtoBody = encodeURIComponent(`De: ${formdata.username} (${formdata.email})\n\nMensaje:\n${formdata.message}`);
      window.location.href = `mailto:oscar_aguilar_apps@outlook.com?subject=${mailtoSubject}&body=${mailtoBody}`;
      setErrorToast(d.contactInfo.alerts.mesageSent, 'i');
      setFormdata({ username: "", email: "", subject: "", message: "", hp_trap: "" });
    } catch (fallbackErr) {
      setErrorToast(d.contactInfo.alerts.generalError, 'e');
    }

    setIsSubmitting(false);
  };

  const handleChange = (event) => {
    setFormdata({
      ...formdata,
      [event.currentTarget.name]: event.currentTarget.value
    });
  };

  const redStar = <span style={{ color: "#cc1122" }}>&nbsp;*</span>;

  return (
    <Layout>
      <div className="mi-contact-area mi-section mi-padding-top mi-padding-bottom">
        <div className="container">
          <Sectiontitle title={d.titles.contact} />
          <div className="row">
            <div className="col-lg-6" style={{ marginBottom: "-15px" }}>
              <Fade triggerOnce delay={50}>
                <div className="mi-contact-formwrapper">
                  <h4>{d.contactInfo.subtitle}</h4>
                  <form action="#" className="mi-form mi-contact-form" onSubmit={submitHandler}>
                    
                    {/* Campo Trampa Honeypot */}
                    <div style={{ display: 'none', opacity: 0, position: 'absolute', left: '-9999px' }} aria-hidden="true">
                      <input
                        type="text"
                        name="hp_trap"
                        tabIndex="-1"
                        autoComplete="off"
                        value={formdata.hp_trap}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="mi-form-field">
                      <label htmlFor="contact-form-name">{d.contactInfo.nameField}{redStar}</label>
                      <input onChange={handleChange} type="text" name="username" id="contact-form-name" value={formdata.username} />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-email">{d.contactInfo.emailField}{redStar}</label>
                      <input onChange={handleChange} type="text" name="email" id="contact-form-email" value={formdata.email} />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-subject">{d.contactInfo.subjectField}{redStar}</label>
                      <input onChange={handleChange} type="text" name="subject" id="contact-form-subject" value={formdata.subject} />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-message">{d.contactInfo.messageField}{redStar}</label>
                      <textarea onChange={handleChange} name="message" id="contact-form-message" cols="30" rows="6" value={formdata.message}></textarea>
                    </div>
                    <div className="mi-form-field">
                      <div style={{ overflow: "hidden", float: "none", marginTop: "10px" }}>
                        <button disabled={isSubmitting} style={{ width: "100%" }} className="mi-button" type="submit">
                          {isSubmitting ? (lang === 'es' ? "Enviando..." : "Sending...") : d.contactInfo.sendButton}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </Fade>
            </div>
            <div className="col-lg-6">
              <div className="mi-contact-info">
                {d.contactInfo.telegram && (
                  <Bounce triggerOnce delay={100}>
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon">
                        <Send />
                      </span>
                      <div className="mi-contact-infoblock-content">
                        <h6>{d.contactInfo.telegramInfo}</h6>
                        <p><a href={d.contactInfo.telegram} rel="noopener noreferrer" target="_blank">@oac28x</a></p>
                      </div>
                    </div>
                  </Bounce>
                )}
                {d.contactInfo.emailAddress && d.contactInfo.emailAddress.length > 0 && (
                  <Bounce triggerOnce delay={200}>
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon">
                        <Mail />
                      </span>
                      <div className="mi-contact-infoblock-content">
                        <h6>{d.contactInfo.emailInfo}</h6>
                        {d.contactInfo.emailAddress.map(email => (
                          <p key={email}><a rel="noopener noreferrer" href={`mailto:${email}`}>{email}</a></p>
                        ))}
                      </div>
                    </div>
                  </Bounce>
                )}
                {d.contactInfo.address && (
                  <Bounce triggerOnce delay={400}>
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon">
                        <MapPin />
                      </span>
                      <div className="mi-contact-infoblock-content">
                        <h6>{d.contactInfo.addressInfo}</h6>
                        <p>{d.contactInfo.address}</p>
                      </div>
                    </div>
                  </Bounce>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </Layout>
  );
}

export default Contact;
